<script>
import dayjs from 'dayjs';
import { mapActions } from 'vuex';
import { LOADING_KEY } from '@/util/constants';
import EzButton from '@/components/ui/Button';
import EzForm from '@/components/ui/Form/EzForm';
import VDatePicker from '@/components/v3/patterns/VDatePicker';
import flash from '@/components/ui/FlashMessage';
import VPdfViewer from "@/components/v3/elements/VPdfViewer";
import VPdfPlaceholder from '@/components/v3/elements/VPdfPlaceholder';

/**
 * VenueContract
 * @version 1.0.0
 * @since 3.32.0
 */

export default {
  name: 'VenueContract',
  components: {
    EzButton,
    EzForm,
    VDatePicker,
    VPdfViewer,
    VPdfPlaceholder,
  },
  props: {
    venueId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      formKey: 'venue-contract',
      loadingKey: LOADING_KEY.FETCH_VENUE_CONTRACT,
      contractDate: null,
      contractFileUrl: null,
      selectedFile: null,
      fileRemoved: false,
      fileType: null,
    };
  },
  computed: {
    additionalData() {
      if (this.fileRemoved) return { contractFile: '' };
      return null;
    },
    hasFile() {
      return this.selectedFile || (this.contractFileUrl && !this.fileRemoved);
    },
    fileName() {
      if (this.selectedFile) return this.selectedFile.name;
      if (this.contractFileUrl) {
        const parts = this.contractFileUrl.split('/');
        return parts[parts.length - 1];
      }
      return null;
    },
  },
  methods: {
    ...mapActions('entities/venues', ['fetchVenueContract']),
    async initValues() {
      const { data } = await this.fetchVenueContract({ venueId: this.venueId });
      const contract = data.data;
      this.contractDate = contract.contractDate ? dayjs(contract.contractDate, 'YYYY-MM-DD') : null;
      this.contractFileUrl = contract.contractFile || null;
      this.fileType = contract.contractFileType
    },
    onDateChange(date) {
      this.contractDate = date;
    },
    onFileChange(e) {
      const file = e.target.files[0] || null;
      this.selectedFile = file;
      if (file) this.fileRemoved = false;
    },
    onRemoveFile() {
      this.selectedFile = null;
      this.fileRemoved = true;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    transformer(data) {
      if (this.contractDate && dayjs(this.contractDate).isValid()) {
        data.set('contractDate', dayjs(this.contractDate).format('YYYY-MM-DD'));
      }
      return data;
    },
    onSuccess() {
      flash.success({ title: 'Contract successfully updated!' });
      this.fileRemoved = false;
      this.selectedFile = null;
      this.initValues();
    },
    onError() {
      flash.error({ title: 'Something went wrong.' });
    },
  },
  created() {
    this.initValues();
  },
};
</script>

<template>
  <div class="container--small">
    <ez-form
      method="put"
      :form-key="formKey"
      :action="`/distributor/venues/${venueId}/contract`"
      submit-type="multipart"
      :transformer="transformer"
      :additional-data="additionalData"
      @success="onSuccess"
      @error="onError"
    >
      <div class="product-settings__section">
        <div class="help-block">
          <h4 class="help-block__title">Contract Date</h4>
          <p class="help-block__text">Assign contract date for this customer.</p>
        </div>
        <v-date-picker
          name="contractDate"
          label="Contract Date"
          v-model="contractDate"
          @dateChange="onDateChange"
        />
      </div>

      <div class="product-settings__section mt-24">
        <div class="help-block">
          <h4 class="help-block__title">Contract File</h4>
          <p class="help-block__text">Upload contract file.</p>
        </div>
        <div class="file-upload">
          <v-pdf-placeholder v-if="!fileType" class="file-upload__preview" />
          <div v-if="!!fileType" class="file-preview__image-container file-upload__preview">
            <a :href="contractFileUrl" target="_blank">
              <img
                v-if="fileType !== 'pdf'"
                class="file-preview__image width-100"
                :src="contractFileUrl"
                alt="Invoice Image"
              />
              <v-pdf-viewer
                class="file-preview__pdf"
                v-if="fileType === 'pdf'"
                :src="contractFileUrl"
                target="_blank"
                :showAllPages="false"
              />
            </a>
          </div>
          <input
            ref="fileInput"
            type="file"
            :name="selectedFile ? 'contractFile' : ''"
            class="file-upload__input"
            @change="onFileChange"
          />
          <ez-button
            type="secondary"
            form-type="button"
            class="file-upload__btn"
            @click="$refs.fileInput.click()"
          >
            {{ hasFile ? 'Change File' : 'Upload File' }}
          </ez-button>
          <ez-button
            v-if="hasFile"
            type="link"
            form-type="button"
            class="file-upload__remove"
            @click="onRemoveFile"
          >
            <font-awesome-icon icon="times" />
            <span>Remove File</span>
          </ez-button>
        </div>
        <p v-if="fileName" class="file-upload__name">{{ fileName }}</p>
      </div>

      <template #buttons>
        <ez-button class="mt-24" form-type="submit">Save Changes</ez-button>
      </template>
    </ez-form>
  </div>
</template>

<style lang="scss" scoped>
$image-height: 64px;
$image-max-width: 120px;

.product-settings__section {
  .help-block {
    margin-bottom: 16px;

    &__title {
      font-weight: 600;
    }

    &__text {
      @include font-size(14px, 20px);
    }
  }
}

.file-upload {
  display: flex;
  align-items: center;

  &__preview {
    height: $image-height;
    width: $image-max-width;
    flex-shrink: 0;
  }

  &__input {
    display: none;
  }

  &__btn {
    margin-left: 1rem;
  }

  &__remove {
    @include font-size(12px);
    font-weight: 400;

    :deep() svg {
      margin-right: 0.5em;
    }
  }

  &__name {
    margin-top: 8px;
    @include font-size(12px);
    color: $color-gray-6C;
  }
}

.file-preview__image-container {
  height: $image-height;
  width: $image-max-width;

  border-radius: 3px;
  margin-right: 16px;
}

:deep() .file-preview {
  display: flex;
  align-items: center;

  &__image {
    height: $image-height;
    max-width: $image-max-width;
    object-fit: cover;

    cursor: pointer;
    border-radius: inherit;
  }

  a {
    display: block;
    width: 100%;
    height: 100%;
  }

  &__pdf {
    display: block;
    height: 100%;
    width: 100%;
    overflow: hidden;
    .pdf-page-canvas {
      width: 100%;
      cursor: pointer !important;
    }
  }
}
</style>
